import { Controller } from "@hotwired/stimulus"
import { NativeOutbox, OUTBOX_CHANGED_EVENT } from "../native_outbox"

// The native outbox on the home (spec §5, §6). In an app with the outbox
// bridge, a link or a text from the compose form always goes to the outbox,
// online or not; the app sends it and the server row replaces it. Files keep
// the normal form submission. Queued items are shown at the top of the
// history from the server-rendered <template> rows (_outbox_templates).
//
// Without the native handlers (the web, iOS 2.1.0) nothing here runs and the
// form posts as before.
export default class extends Controller {
  static targets = [ "list", "template", "error" ]
  static values = { untitledText: String, enqueueFailed: String }

  connect() {
    if (!NativeOutbox.supported) return

    this.paused = null
    this.onChanged = () => this.load()
    document.addEventListener(OUTBOX_CHANGED_EVENT, this.onChanged)
    this.load()
  }

  disconnect() {
    if (this.onChanged) document.removeEventListener(OUTBOX_CHANGED_EVENT, this.onChanged)
  }

  async load() {
    if (!NativeOutbox.supported) return

    try {
      const { items, paused } = await NativeOutbox.getOutbox()
      this.paused = paused
      this.render(items)
    } catch (error) {
      console.warn("Could not read the outbox", error)
    }
  }

  // Bubbles up from the compose form before Turbo sees it.
  async submit(event) {
    if (!NativeOutbox.supported) return

    const form = event.target
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-controller~="upload-form"]')) return

    const item = this.itemFrom(new FormData(form))
    if (!item) return

    event.preventDefault()
    // The app answers after its write, which can wait for the share
    // extension's. A second tap meanwhile would queue the item twice.
    if (this.enqueuing) return
    this.enqueuing = true
    this.hideError()

    const submitted = this.fieldValues(form)
    let reply
    try {
      reply = await NativeOutbox.enqueue(item)
    } catch (error) {
      reply = { ok: false, error: null }
      console.warn("Could not add to the outbox", error)
    } finally {
      this.enqueuing = false
    }

    if (reply?.ok) {
      this.resetForm(form, submitted)
      this.render(reply.items || [])
    } else {
      this.stopSendAnimation(form)
      this.showError(reply?.error)
    }
  }

  async retry({ params: { clientId } }) {
    if (!clientId) return

    const reply = await NativeOutbox.retry(clientId)
    if (reply?.ok === false) this.showError(reply.error)
    this.render(reply?.items || [])
  }

  async delete({ params: { clientId } }) {
    if (!clientId) return

    const reply = await NativeOutbox.delete(clientId)
    this.render(reply?.items || [])
  }

  // The hidden upload_type follows the active tab (upload_form_controller.js).
  itemFrom(data) {
    const type = data.get("upload[upload_type]")
    const title = (data.get("upload[title]") || "").trim() || null

    if (type === "link") {
      const sourceURL = (data.get("upload[source_url]") || "").trim()
      return sourceURL ? { kind: "link", sourceURL } : null
    }
    if (type === "text") {
      const bodyText = (data.get("upload[body_text]") || "").trim()
      // The app's outbox carries no author (iOS 2.2.0): a text with one posts
      // the form, so it needs the network.
      if ((data.get("upload[author]") || "").trim()) return null
      return bodyText ? { kind: "text", bodyText, title } : null
    }
    return null
  }

  render(items) {
    if (!this.hasListTarget) return

    this.listTarget.querySelectorAll("[data-outbox-item]").forEach((row) => row.remove())
    // Oldest first from the app; each prepend lands on top, so the newest leads.
    for (const item of items) {
      const row = this.rowFor(item)
      if (row) this.listTarget.prepend(row)
    }
    document.getElementById("uploads-empty-state")?.classList.toggle("hidden", items.length > 0)
  }

  rowFor(item) {
    const template = this.templateTargets.find((element) => element.dataset.outboxState === this.stateFor(item))
    const row = template?.content.firstElementChild?.cloneNode(true)
    if (!row) return null

    row.dataset.outboxClientId = item.client_id
    row.querySelector("[data-outbox-title]").textContent = this.titleFor(item)
    row.querySelectorAll("[data-outbox-icon]").forEach((icon) => {
      if (icon.dataset.outboxIcon !== (item.kind === "text" ? "text" : "link")) icon.remove()
    })
    const message = row.querySelector("[data-outbox-error]")
    if (message) message.textContent = item.error?.message || ""
    row.querySelectorAll("[data-action]").forEach((button) => {
      button.dataset.outboxClientIdParam = item.client_id
    })
    return row
  }

  // Paused rows are derived from the app's auth state, not stored (spec §5.1).
  stateFor(item) {
    if (this.paused === "other_account") return "other_account"
    if (item.state === "rejected") return "rejected"
    if (this.paused === "sign_in") return "sign_in"
    return "queued"
  }

  titleFor(item) {
    return item.title || item.source_url || (item.body_text || "").slice(0, 80) || this.untitledTextValue
  }

  fieldValues(form) {
    return new Map([ ...form.querySelectorAll("input, textarea") ].map((field) => [ field, field.value ]))
  }

  // A field edited while the app was writing keeps the new text.
  resetForm(form, submitted) {
    const edited = [ ...submitted ].filter(([ field, value ]) => field.value !== value).map(([ field ]) => [ field, field.value ])
    form.reset()
    for (const [ field, value ] of edited) field.value = value

    form.querySelectorAll("input, textarea").forEach((field) => {
      field.dispatchEvent(new Event("input", { bubbles: true }))
    })
  }

  // upload_form_controller.js starts the send animation on submit and stops it
  // only on a failed Turbo submission, which an outbox submit never makes.
  stopSendAnimation(form) {
    form.querySelector('[data-upload-form-target="sendIcon"]')?.classList.remove("sending")
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message || this.enqueueFailedValue
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
